import React, { useCallback, useMemo } from 'react';
import { useLocalization } from '@/contexts/LocalizationContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const languages = [
  { code: 'en' as const, name: 'English', shortCode: 'EN' },
  { code: 'ar' as const, name: 'العربية', shortCode: 'AR' },
] as const;

// Get target language (the one to switch to)
const getTargetLanguage = (currentLocale: string) => {
  return currentLocale === 'ar'
    ? languages.find((lang) => lang.code === 'en')!
    : languages.find((lang) => lang.code === 'ar')!;
};

// Get tooltip text based on current language
const getTooltipText = (currentLocale: string) => {
  if (currentLocale === 'ar') {
    return 'Switch to English';
  }
  return 'التبديل إلى العربية';
};

export const RequestHeader: React.FC = () => {
  const { locale, setLocale, t } = useLocalization();

  const targetLanguage = useMemo(() => getTargetLanguage(locale), [locale]);
  const tooltipText = useMemo(() => getTooltipText(locale), [locale]);

  const buttonClasses = useMemo(
    () =>
      `inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        locale === 'ar'
          ? 'bg-[#EF4354] hover:bg-[#d93847] focus:ring-[#EF4354]'
          : 'bg-[#10B981] hover:bg-[#059669] focus:ring-[#10B981]'
      }`,
    [locale]
  );

  const toggleLanguage = useCallback(() => {
    const newLang = locale === 'ar' ? 'en' : 'ar';
    setLocale(newLang);
  }, [locale, setLocale]);

  const isRTL = locale === 'ar';

  const LogoAndHome = (
    <div className="flex items-center gap-4 sm:gap-6 lg:gap-10">
      <a
        href="https://wtdcare.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#131375] rounded-md p-1 -m-1"
        aria-label="WTDcare Home"
      >
        <img
          src="/assets/header/Form Page - Logo & Icons-02.svg"
          alt="WTDcare"
          className="h-7 sm:h-10 w-auto"
          loading="eager"
          width="32"
          height="32"
        />
      </a>
      <nav className="hidden sm:block text-sm">
        <a
          href="https://wtdcare.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1F2937] font-medium hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors focus:outline-none"
        >
          {t('nav.home')}
        </a>
      </nav>
    </div>
  );

  const PhoneAndLanguage = (
    <div className="flex items-center gap-3 sm:gap-4">
      <a
        href="tel:920033297"
        className="text-base sm:text-lg font-sans font-medium text-black hover:text-[rgb(49,133,252)] transition-colors whitespace-nowrap"
        aria-label="Call 920033297"
      >
        920033297
      </a>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={toggleLanguage}
              aria-label={`Switch to ${targetLanguage.name}`}
              className={buttonClasses}
            >
              <span>{targetLanguage.shortCode}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="bg-gray-900 text-white px-3 py-2 text-sm font-medium"
          >
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  return (
    <header className="w-full bg-white" dir="ltr">
      {/* Top blue bar */}
      <div className="h-1.5 sm:h-6 bg-[#131375]" />

      {/* White header - Always LTR layout */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 py-4">
          {isRTL ? (
            <>
              <div className="flex items-center gap-3 sm:gap-4">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={toggleLanguage}
                        aria-label={`Switch to ${targetLanguage.name}`}
                        className={buttonClasses}
                      >
                        <span>{targetLanguage.shortCode}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="bg-gray-900 text-white px-3 py-2 text-sm font-medium"
                    >
                      {tooltipText}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <a
                  href="tel:920033297"
                  className="text-base sm:text-lg font-sans font-medium text-black hover:text-[rgb(49,133,252)] transition-colors whitespace-nowrap"
                  aria-label="Call 920033297"
                >
                  920033297
                </a>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 lg:gap-10">
                <nav className="hidden sm:block text-sm">
                  <a
                    href="https://wtdcare.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1F2937] font-medium hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors focus:outline-none"
                  >
                    {t('nav.home')}
                  </a>
                </nav>
                <a
                  href="https://wtdcare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#131375] rounded-md p-1 -m-1"
                  aria-label="WTDcare Home"
                >
                  <img
                    src="/assets/header/Form Page - Logo & Icons-02.svg"
                    alt="WTDcare"
                    className="h-7 sm:h-10 w-auto"
                    loading="eager"
                    width="32"
                    height="32"
                  />
                </a>
              </div>
            </>
          ) : (
            <>
              {LogoAndHome}
              {PhoneAndLanguage}
            </>
          )}
        </div>
      </div>
    </header>
  );
};
